import { format, addDay } from '@formkit/tempo'

export function shouldDisableDate(
  date: Date,
  minDate?: Date,
  maxDate?: Date,
  unavailableDates?: Set<string> | string[]
): boolean {
  if (!isValidDate(date)) return false
  if (minDate && isValidDate(minDate) && date < startOfDay(minDate)) return true
  if (maxDate && isValidDate(maxDate) && date > endOfDay(maxDate)) return true

  if (unavailableDates && containsDate(unavailableDates, date)) return true
  return false
}

export function computeBoundsFromSlots(slots: Array<{ TimeSlotStart: string; TimeSlotEnd?: string }>) {
  // Fallback window when no slots are available
  if (!slots || slots.length === 0) {
    const today = startOfDay(new Date())
    return {
      minDate: addDay(today, 1),
      maxDate: addDay(today, 365)
    }
  }

  // Earliest start day among all slots
  const startDays: Date[] = slots
    .map(s => startOfDay(new Date(s.TimeSlotStart)))
    .filter(d => isValidDate(d))

  // Latest end day among all slots (fallback to start if end missing)
  const endDays: Date[] = slots
    .map(s => startOfDay(new Date(s.TimeSlotEnd || s.TimeSlotStart)))
    .filter(d => isValidDate(d))

  if (startDays.length === 0 || endDays.length === 0) {
    const today = startOfDay(new Date())
    return { minDate: addDay(today, 1), maxDate: addDay(today, 365) }
  }

  const min = new Date(Math.min(...startDays.map(d => d.getTime())))
  const max = new Date(Math.max(...endDays.map(d => d.getTime())))

  // Return exact day bounds; shouldDisableDate uses endOfDay(max) for inclusive check
  return { minDate: min, maxDate: max }
}

export function toUnavailableSet(unavailableDates: string[] | Set<string>) {
  return unavailableDates instanceof Set ? unavailableDates : new Set(unavailableDates)
}

function containsDate(list: Set<string> | string[], date: Date) {
  if (!isValidDate(date)) return false
  const sod = startOfDay(date)
  if (!isValidDate(sod)) return false
  const key = format(sod, 'YYYY-MM-DD')
  if (list instanceof Set) return list.has(key)
  return list.includes(key)
}

function startOfDay(d: Date) {
  if (!isValidDate(d)) return d as unknown as Date
  const date = new Date(d)
  date.setHours(0, 0, 0, 0)
  return date
}

function endOfDay(d: Date) {
  if (!isValidDate(d)) return d as unknown as Date
  const date = new Date(d)
  date.setHours(23, 59, 59, 999)
  return date
}

function isValidDate(d: any): d is Date {
  return d instanceof Date && !isNaN(d.getTime())
}
